import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useLoginMutation } from '@/store/api/authApi';
import { tokenStorage } from '@/lib/tokens';
import { t } from '@/lib/i18n';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card } from '@/components/ui/Card';
import type { ApiErrorBody } from '@/types/auth';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';

const schema = z.object({
  email: z.string().email(),
  password: z.string().min(8),
  totp: z.string().optional(),
});

type FormValues = z.infer<typeof schema>;

function extractErrorMessage(error: unknown): string | null {
  if (typeof error === 'object' && error !== null && 'data' in error) {
    const data = (error as FetchBaseQueryError).data as ApiErrorBody | undefined;
    return data?.message ?? null;
  }
  return null;
}

export function LoginPage() {
  const navigate = useNavigate();
  const [login, { isLoading }] = useLoginMutation();
  const [error, setError] = useState<string | null>(null);
  const [needsTotp, setNeedsTotp] = useState(false);
  const { register, handleSubmit, formState: { errors } } = useForm<FormValues>({
    resolver: zodResolver(schema),
  });

  const onSubmit = async (values: FormValues) => {
    try {
      setError(null);
      const response = await login(values).unwrap();
      tokenStorage.set(response.accessToken, response.refreshToken);
      navigate('/dashboard', { replace: true });
    } catch (caught) {
      const message = extractErrorMessage(caught);
      if (message?.includes('TOTP')) {
        setNeedsTotp(true);
        setError(t('auth.login.error.totp'));
        return;
      }
      setError(t('auth.login.error.invalid'));
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 p-6">
      <Card className="w-full max-w-sm p-6">
        <h1 className="mb-6 text-xl font-semibold">{t('auth.login.title')}</h1>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label className="text-sm font-medium" htmlFor="login-email">{t('auth.login.email')}</label>
            <Input id="login-email" type="email" autoComplete="email" {...register('email')} />
            {errors.email && <p className="mt-1 text-xs text-destructive" role="alert">{errors.email.message}</p>}
          </div>
          <div>
            <label className="text-sm font-medium" htmlFor="login-password">{t('auth.login.password')}</label>
            <Input id="login-password" type="password" autoComplete="current-password" {...register('password')} />
            {errors.password && <p className="mt-1 text-xs text-destructive" role="alert">{errors.password.message}</p>}
          </div>
          {needsTotp && (
            <div>
              <label className="text-sm font-medium" htmlFor="login-totp">{t('auth.login.totp')}</label>
              <Input id="login-totp" inputMode="numeric" autoComplete="one-time-code" maxLength={6} {...register('totp')} />
            </div>
          )}
          {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
          <Button type="submit" disabled={isLoading} className="w-full">
            {isLoading ? 'Ingresando...' : t('auth.login.submit')}
          </Button>
          <div className="text-center text-sm">
            <Link to="/forgot-password" className="text-primary hover:underline">
              {t('auth.login.forgot')}
            </Link>
          </div>
        </form>
      </Card>
    </div>
  );
}
